import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import "../../../styles/pages/ouber.scss";
import { Space, Button, Typography, Input, Row, Col, InputNumber, Select, Modal, Tooltip, Tour } from "antd";
import {
  EyeOutlined,
  EditOutlined,
  PlusCircleOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
  DeleteOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import PvTable from "../../../components/Table/PvTable";
import DataView from "../../../components/DataView/DataView";
import PvCardList from "../../../components/DataView/PvCardList";
import AddVehicleModal from "../../../components/Ouber/modals/admin/AdminAddVehicleModal";
import AdminViewVehicleModal from "../../../components/Ouber/modals/admin/AdminViewVehicleModal";
import { showErrorToast, showSuccessToast } from "../../../components/Ouber/ToastNotification";
import OuberPageInfoIcon from "../../../components/Ouber/OuberPageInfoIcon";
import { useOuberService } from "../../../services";
import { mapUnitToAcronym } from "../../../utils/ouberUtils";

/**
 * The purpose of this page is to manage the fleet of vehicles, 
 * track plate numbers, and monitor their passenger capacities.
 */
const ManageVehiclePage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const { dataView } = useSelector((state) => state.generalSlice);
  const { details: profile, account_types = [] } = useSelector((state) => state.accountDetailSlice);
  const isSuperAdmin = profile?.account_type === "Super Admin" || account_types.includes("superadmin") || account_types.includes("xadmin");
  const canManage = (record) => isSuperAdmin || (record.created_by_employee_id && record.created_by_employee_id === profile?.id);

  const [filterModel, setFilterModel] = useState("");
  const [filterPlate, setFilterPlate] = useState("");
  const [filterCapacity, setFilterCapacity] = useState(null);
  const [filterType, setFilterType] = useState(null);
  const [isTourOpen, setIsTourOpen] = useState(false);
  const createVehicleButtonRef = useRef(null);
  const filtersRef = useRef(null);
  const viewControlsRef = useRef(null);
  const tableRef = useRef(null);

  const { service: ouberService, loading } = useOuberService();
  const [data, setData] = useState([]);

  const tourSeenStorageKey = profile?.email
    ? `ouber-vehicles-tour-seen:${profile.email}`
    : null;

  useEffect(() => {
    if (!tourSeenStorageKey) return;

    try {
      if (!window.localStorage.getItem(tourSeenStorageKey)) {
        setIsTourOpen(true);
      }
    } catch {
      // localStorage can be unavailable (private browsing, etc.) — the tour
      // simply won't auto-open in that case, no need to fail loudly.
    }
  }, [tourSeenStorageKey]);

  const handleTourClose = () => {
    setIsTourOpen(false);

    if (!tourSeenStorageKey) return;

    try {
      window.localStorage.setItem(tourSeenStorageKey, "1");
    } catch {
      // Ignore — worst case the tour auto-opens again next visit.
    }
  };

  const tourSteps = useMemo(
    () => [
      {
        title: "Add a vehicle",
        description:
          "Register a new vehicle, including its plate number, type, and passenger capacity.",
        target: () => createVehicleButtonRef.current,
      },
      {
        title: "Search and filter",
        description:
          "Narrow the list down by model, plate number, type, or minimum capacity.",
        target: () => filtersRef.current,
      },
      {
        title: "Table or card view",
        description: "Switch layouts, or refresh the list to pull the latest data.",
        target: () => viewControlsRef.current,
      },
      {
        title: "View, edit, or remove a vehicle",
        description:
          "Use the icons on each row to view details, edit info, or delete a vehicle (deleting requires Super Admin).",
        target: () => tableRef.current,
      },
    ],
    [],
  );

  // Retrieves the vehicle roster from the backend with filters for model, plate, capacity, and type
  const fetchVehicles = useCallback(async () => {
    try {
      const params = {
        search_model: filterModel || undefined,
        plate_number: filterPlate || undefined,
        min_capacity: filterCapacity !== null ? filterCapacity : undefined,
        vehicle_type: filterType || undefined,
      };
      const response = await ouberService.getVehicles(params);
      
      const vehiclesList = Array.isArray(response) ? response : (response?.data || []);

      const vehicles = vehiclesList.map(v => ({ ...v, key: v.vehicle_id.toString(), id: v.vehicle_id.toString() }));
      
      // Formats raw vehicle data into a consistent UI-friendly object structure
      setData(vehicles);
    } catch (err) {
      showErrorToast("Fetch Error", "Failed to load vehicles.");
    }
  }, [filterModel, filterPlate, filterCapacity, filterType, ouberService]);

  const fetchVehiclesRef = useRef(fetchVehicles);
  useEffect(() => {
    fetchVehiclesRef.current = fetchVehicles;
  }, [fetchVehicles]);

  // Implements a debounced fetch to handle live search filtering for the vehicle list
  useEffect(() => {
    const handler = setTimeout(() => {
      fetchVehiclesRef.current();
    }, 500);
    return () => clearTimeout(handler);
  }, [filterModel, filterPlate, filterCapacity, filterType]);



  const filteredData = data;

  const handleEditVehicle = (vehicle) => {
    setSelectedVehicle(vehicle);
    setIsModalOpen(true);
  };

  const handleViewVehicle = (vehicle) => {
    setSelectedVehicle(vehicle);
    setIsViewModalOpen(true);
  };

  const handleCreateVehicle = () => {
    setSelectedVehicle(null);
    setIsModalOpen(true);
  };

  const handleDeleteVehicle = (vehicle) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this vehicle?',
      content: `This will permanently remove ${vehicle.vehicle_model} (${vehicle.plate_number}) from the registry.`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await ouberService.deleteVehicle(vehicle.id);
          showSuccessToast("Success", "Vehicle deleted successfully.");
          fetchVehicles();
        } catch (e) {
          showErrorToast("Error", "Failed to delete vehicle. It might be assigned to a trip.");
        }
      }
    });
  };

  const handleSaveVehicle = async (values) => {
    try {
      if (selectedVehicle) {
        await ouberService.updateVehicle(selectedVehicle.id, values);
        showSuccessToast("Success", "Vehicle updated successfully.");
      } else {
        await ouberService.createVehicle(values);
        showSuccessToast("Success", "Vehicle created successfully.");
      }
      setIsModalOpen(false);
      fetchVehicles();
    } catch (err) {
      showErrorToast("Error", "Failed to save vehicle.");
    }
  };

  const columns = [
    { title: "Vehicle ID", dataIndex: "id", key: "id", align: "center" },
    { title: "Vehicle Model", dataIndex: "vehicle_model", key: "vehicle_model", align: "center" },
    { title: "Vehicle Type", dataIndex: "vehicle_type", key: "vehicle_type", align: "center" },
    {
      title: "Plate Number",
      dataIndex: "plate_number",
      key: "plate_number",
      align: "center",
    },
    {
      title: "Capacity",
      dataIndex: "max_passenger_capacity",
      key: "max_passenger_capacity",
      align: "center",
      render: (capacity) => `${capacity}`
    },
    {
      title: "Office",
      key: "office",
      align: "center",
      render: (_, record) => (record.office && mapUnitToAcronym(record.office))
        || <span style={{ color: "#bfbfbf", fontSize: "12px" }}>—</span>,
    },
    {
      title: "Created By",
      key: "created_by",
      align: "center",
      render: (_, record) => record.created_by
        ? `${record.created_by.first_name || ""} ${record.created_by.last_name || ""}`.trim()
        : <span style={{ color: "#bfbfbf", fontSize: "12px" }}>—</span>,
    },
    {
      title: "Next Assignment",
      key: "next_assignment",
      align: "center",
      render: (_, record) => {
        const next = record.next_assignment;
        if (!next) return <span style={{ color: "#bfbfbf", fontSize: "12px" }}>None Scheduled</span>;
        
        return (
          <div style={{ fontSize: "12px", lineHeight: "1.2" }}>
            <div style={{ fontWeight: "bold", color: "#862633" }}>
              {next.trip_date ? next.trip_date.split('T')[0] : 'N/A'}
            </div>
            <div style={{ color: "#595959" }}>{next.dropoff_location || next.pickup_venue}</div>
          </div>
        );
      }
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Space size="middle">
          <EyeOutlined
            style={{ fontSize: "18px", cursor: "pointer", color: "#1890ff" }}
            onClick={() => handleViewVehicle(record)}
          />
          {canManage(record) && (
            <EditOutlined
              style={{ fontSize: "18px", cursor: "pointer", color: "#52c41a" }}
              onClick={() => handleEditVehicle(record)}
            />
          )}
          {canManage(record) && (
            <DeleteOutlined
              style={{ fontSize: "18px", cursor: "pointer", color: "#f5222d" }}
              onClick={() => handleDeleteVehicle(record)}
            />
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="ouber-page-container">
      <Space className="ouber-page-header">
        <Typography.Title level={3} className="ouber-page-title">
          Manage Vehicles
          <OuberPageInfoIcon
            message="Vehicle Registry"
            description="View, edit, and manage system-wide vehicles, including their plate numbers, minimum capacities, and next trip assignments."
          />
        </Typography.Title>
        <Space>
          <Button
            ref={createVehicleButtonRef}
            type="primary"
            icon={<PlusCircleOutlined />}
            className="ouber-btn-forest"
            onClick={handleCreateVehicle}
          >
            Create Vehicle
          </Button>
          <Tooltip title="Take a tour of this page">
            <Button
              icon={<QuestionCircleOutlined />}
              onClick={() => setIsTourOpen(true)}
            />
          </Tooltip>
        </Space>
      </Space>

      <div className="ouber-filter-container" ref={filtersRef}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Input 
              placeholder="Search Vehicle Model" 
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              value={filterModel}
              onChange={(e) => setFilterModel(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Input 
              placeholder="Search Plate Number" 
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              value={filterPlate}
              onChange={(e) => setFilterPlate(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filter by Type"
              style={{ width: "100%" }}
              allowClear
              value={filterType}
              onChange={(val) => setFilterType(val)}
              options={[
                { value: "Car Rental", label: "Car Rental" },
                { value: "Carpool", label: "Carpool" },
                { value: "University Owned", label: "University Owned" },
                { value: "Outsourced", label: "Outsourced" },
              ]}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <InputNumber
              style={{ width: "100%" }}
              placeholder="Min Capacity"
              min={1}
              precision={0}
              onKeyPress={(event) => {
                if (!/[0-9]/.test(event.key)) {
                  event.preventDefault();
                }
              }}
              value={filterCapacity}
              onChange={(val) => setFilterCapacity(val)}
            />
          </Col>
        </Row>
      </div>

      <div ref={viewControlsRef} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: "12px" }}>
        <DataView />
        <Button
          icon={<SyncOutlined />}
          onClick={fetchVehicles}
          loading={loading}
          className="ouber-btn-primary"
          style={{ marginBottom: 0 }}
        >
          Refresh
        </Button>
      </div>

      <div ref={tableRef}>
        {dataView === "table" ? (
          <PvTable
            data={filteredData}
            columns={columns}
            loading={loading}
            onRowClick={() => {}}
          />
        ) : (
          <PvCardList
            data={filteredData}
            columns={columns}
            onRowClick={() => {}}
            loading={loading}
            maxActiveRow={filteredData.length}
          />
        )}
      </div>

      <AddVehicleModal
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onSave={handleSaveVehicle}
        initialValues={selectedVehicle}
      />

      <AdminViewVehicleModal
        open={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        vehicle={selectedVehicle}
      />

      <Tour open={isTourOpen} onClose={handleTourClose} steps={tourSteps} />
    </div>
  );
};

export default ManageVehiclePage;
